import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, XCircle } from "lucide-react";

export default function PublicResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 text-center">
        <Card>
          <CardContent className="pt-8 pb-8">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mb-3">
              <XCircle size={26} className="text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-gray-900">Invalid link</h1>
            <p className="text-sm text-gray-600 mt-2">
              This password-reset link is missing or malformed. Please request a new one.
            </p>
            <Link to="/site/forgot-password" className="inline-block mt-6">
              <Button variant="outline">Request new link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/public/customers/password-reset/confirm", {
        token, new_password: pw,
      });
      setDone(true);
      setTimeout(() => nav("/site/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 text-center">
        <Card>
          <CardContent className="pt-8 pb-8">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 mb-3">
              <CheckCircle2 size={26} className="text-emerald-700" />
            </div>
            <h1 className="font-display text-2xl font-bold text-gray-900">Password updated</h1>
            <p className="text-sm text-gray-600 mt-2">Redirecting you to sign-in…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 mb-3">
          <KeyRound size={26} className="text-emerald-800" />
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Choose a new password
        </h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">New password</Label>
              <Input required type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                data-testid="reset-password" />
              <div className="text-[11px] text-gray-500 mt-1">At least 6 characters.</div>
            </div>
            <div>
              <Label className="text-xs">Confirm new password</Label>
              <Input required type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                data-testid="reset-password-confirm" />
            </div>
            <Button
              type="submit"
              disabled={busy || !pw || !pw2}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
              data-testid="reset-submit">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
